import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { User } from '../../models/User';
import { formatDate } from '../../utils/DateFormatter';
import { MailIcon, PhoneIcon, CakeIcon, LocationMarkerIcon } from '@heroicons/react/outline';
import { ChevronDownIcon, ChevronRightIcon, UsersIcon, BriefcaseIcon } from '@heroicons/react/solid';
import { motion, AnimatePresence } from 'framer-motion';

interface UserTableProps {
  users: User[];
  isAdmin: boolean;
  isContactsLoaded: boolean;
  searchQuery: string;
}

const ContactCard = ({ user }: { user: User }) => {
  const initials = `${user.firstName?.charAt(0)}${user.lastName?.charAt(0)}`;
  const isAdvisor = !!user.advisor;
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  const formatCellNumber = (num: string): string => {
    const numStr = String(num);
    return numStr.length === 10
      ? `${numStr.substring(0, 3)}-${numStr.substring(3, 6)}-${numStr.substring(6, 10)}`
      : numStr;
  };

  const formattedBirthday = formatDate(user.birthday);
  const hasBirthday = formattedBirthday !== 'N/A';
  const hasCell = user.cellNumber && user.cellNumber.trim() !== '' && user.cellNumber !== 'N/A';
  const hasEmail = user.email && user.email.trim() !== '' && user.email !== 'N/A';
  const hasAddress = user.streetAddress && user.streetAddress.trim() !== '' && user.streetAddress !== 'N/A';

  const hasAnyActions = hasEmail || (!isAdvisor && hasCell);

  return (
    <div className="bg-white dark:bg-slate-800/40 sm:backdrop-blur-xl rounded-2xl p-6 border border-slate-200 dark:border-white/5 shadow-lg shadow-slate-200/50 dark:shadow-none sm:hover:shadow-2xl sm:hover:-translate-y-1 transition-all duration-300 group flex flex-col h-full">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`h-14 w-14 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg shadow-blue-500/30 bg-gradient-to-br ${isAdvisor ? 'from-slate-600 to-slate-800' : 'from-blue-600 to-indigo-700'}`}>
            {initials}
          </div>
          <div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">{user.firstName} {user.lastName}</h3>
            <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">
              {isAdvisor ? 'Council Advisor' : 'Family Member'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 mb-8 flex-grow">
        {hasEmail && (
          <a href={`mailto:${user.email}`} className="flex items-center gap-3 group/item">
            <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover/item:text-blue-500 transition-colors">
              <MailIcon className="h-4 w-4" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300 truncate">{user.email}</span>
          </a>
        )}
        
        {hasCell && (
          <a href={`tel:${user.cellNumber}`} className="flex items-center gap-3 group/item">
            <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover/item:text-green-500 transition-colors">
              <PhoneIcon className="h-4 w-4" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{formatCellNumber(user.cellNumber)}</span>
          </a>
        )}

        {hasBirthday && (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400">
              <CakeIcon className="h-4 w-4" />
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{formattedBirthday}</span>
          </div>
        )}

        {hasAddress && (
          <a 
            href={`https://www.google.com/maps/search/?api=1&query=${user.streetAddress}+${user.city}+${user.stateText}+${user.zipCode}`}
            target="_blank" 
            rel="noreferrer"
            className="flex items-start gap-3 group/item hover:bg-slate-50 dark:hover:bg-white/5 p-2 -m-2 rounded-xl transition-colors"
          >
            <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover/item:text-orange-500 transition-colors mt-0.5">
              <LocationMarkerIcon className="h-4 w-4" />
            </div>
            <span className="text-sm font-bold text-slate-600 dark:text-slate-300 leading-relaxed group-hover/item:text-blue-600 dark:group-hover:item:text-blue-400 transition-colors underline decoration-slate-300 dark:decoration-slate-700 underline-offset-4 decoration-dashed">
              {user.streetAddress}<br />{user.city}, {user.stateText} {user.zipCode}
            </span>
          </a>
        )}
      </div>

      {hasAnyActions && (
        <div className="flex gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
          {hasEmail && (
            <a href={`mailto:${user.email}`} className="flex-1 py-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-black uppercase tracking-widest text-center hover:bg-blue-600 hover:text-white transition-all">
              Email
            </a>
          )}
          {isMobile && !isAdvisor && hasCell && (
            <a href={`tel:${user.cellNumber}`} className="flex-1 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-black uppercase tracking-widest text-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">
              Call
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export const UserTable = (props: UserTableProps) => {
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filteredAdvisors, setFilteredAdvisors] = useState<User[]>([]);
  const [isFamilyExpanded, setIsFamilyExpanded] = useState(false);
  const [isAdvisorsExpanded, setIsAdvisorsExpanded] = useState(false);
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    if (!isEmpty(props.users)) {
      const lowerCaseQuery = props.searchQuery.toLowerCase();

      const filterAndSort = (userList: User[]) => {
        return userList
          .filter((user) => {
            if (isEmpty(lowerCaseQuery)) return true;
            return (
              (user.firstName || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.lastName || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.email || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.city || '').toLowerCase().includes(lowerCaseQuery) ||
              (user.stateText || '').toLowerCase().includes(lowerCaseQuery)
            );
          })
          .sort((a, b) => a.firstName.localeCompare(b.firstName));
      };

      const familyResults = filterAndSort(props.users.filter((user) => !user.advisor));
      const advisorResults = filterAndSort(props.users.filter((user) => user.advisor));

      setFilteredUsers(familyResults);
      setFilteredAdvisors(advisorResults);

      // Auto-expand sections if searching and results are found
      if (!isEmpty(props.searchQuery)) {
        if (!isEmpty(familyResults)) setIsFamilyExpanded(true);
        if (!isEmpty(advisorResults)) setIsAdvisorsExpanded(true);
      } else {
        // Optional: collapse when search is cleared? The user wanted collapsed by default.
        setIsFamilyExpanded(false);
        setIsAdvisorsExpanded(false);
      }
    }
  }, [props.users, props.searchQuery]);

  return (
    <div className="w-full">
      {!props.isContactsLoaded ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="bg-white dark:bg-slate-800/40 rounded-[2rem] p-8 h-80 animate-pulse border border-slate-100 dark:border-slate-800" />
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Family Section */}
          <div>
            <button 
              onClick={() => setIsFamilyExpanded(!isFamilyExpanded)}
              className={`w-full flex items-center justify-between p-6 rounded-[2.5rem] border transition-all group ${
                isFamilyExpanded 
                  ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-500/20' 
                  : 'bg-white dark:bg-slate-800/40 border-slate-200 dark:border-white/5 text-slate-900 dark:text-white hover:border-slate-500/50 hover:shadow-lg hover:bg-slate-50 dark:hover:bg-white/5 sm:hover:-translate-y-1'
                  }`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-2xl ${isFamilyExpanded ? 'bg-white/20' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'}`}>
                  <UsersIcon className="h-6 w-6" />
                </div>
                <div className="text-left">
                  <span className="block font-black uppercase tracking-widest text-lg sm:text-xl leading-none">Family Members</span>
                  <span className={`text-[10px] font-black uppercase tracking-[0.2em] mt-1.5 block ${isFamilyExpanded ? 'text-white/60' : 'text-slate-400'}`}>
                    {filteredUsers.length} Family Records
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isFamilyExpanded ? <ChevronDownIcon className="h-7 w-7" /> : <ChevronRightIcon className="h-7 w-7 opacity-30" />}
              </div>
            </button>

            <AnimatePresence>
              {isFamilyExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: isMobile ? 0.3 : 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="pt-8 pl-2 sm:pl-6">
                    {isEmpty(filteredUsers) ? (
                      <div className="py-20 text-center bg-slate-50 dark:bg-slate-900/20 rounded-[2.5rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
                        <p className="text-slate-500 font-bold tracking-tight">No family members found matching your search.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredUsers.map((user, i) => (
                          <ContactCard key={user.cognitoUserId || i} user={user} />
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Advisors Section */}
          {(!isEmpty(filteredAdvisors) || isEmpty(props.searchQuery)) && (
            <div>
              <button 
                onClick={() => setIsAdvisorsExpanded(!isAdvisorsExpanded)}
                className={`w-full flex items-center justify-between p-6 rounded-[2.5rem] border transition-all group ${
                  isAdvisorsExpanded 
                    ? 'bg-slate-800 border-slate-800 text-white shadow-xl shadow-slate-900/20' 
                    : 'bg-white dark:bg-slate-800/40 border-slate-200 dark:border-white/5 text-slate-900 dark:text-white hover:border-slate-500/50 hover:shadow-lg hover:bg-slate-50 dark:hover:bg-white/5 sm:hover:-translate-y-1'
                    }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-2xl ${isAdvisorsExpanded ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'}`}>
                    <BriefcaseIcon className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <span className="block font-black uppercase tracking-widest text-lg sm:text-xl leading-none">Council Advisors</span>
                    <span className={`text-[10px] font-black uppercase tracking-[0.2em] mt-1.5 block ${isAdvisorsExpanded ? 'text-white/60' : 'text-slate-400'}`}>
                      {filteredAdvisors.length} Professional Partners
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {isAdvisorsExpanded ? <ChevronDownIcon className="h-7 w-7" /> : <ChevronRightIcon className="h-7 w-7 opacity-30" />}
                </div>
              </button>

              <AnimatePresence>
                {isAdvisorsExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: isMobile ? 0.3 : 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <div className="pt-8 pl-2 sm:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredAdvisors.map((advisor, i) => (
                          <ContactCard key={advisor.cognitoUserId || i} user={advisor} />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
